import * as React from "react";
import PropTypes from "prop-types";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { isFailure } from "typescript-fsa-redux-thunk";
import styled from "styled-components";
import { hot } from "react-hot-loader";
import { detect } from "detect-browser";
import isEqual from "lodash/isEqual";

import { RootState } from "../../state";
import { reducer } from "../reducers";
import draftChangesMiddleware from "../middlewares/draftChangesMiddleware";
import { reducerRegistry } from "../../lib/ReducerRegistry";
import { media } from "../../common/utils/style";
import {
  editModeStarted,
  editModeFinished,
  draftCompanyUpdated,
  draftCompanyAvatarUpdated,
  draftCompanyBackgroundUpdated,
  saveDraftAllWorker,
  draftReset,
} from "../actions";
import { pushState } from "../../common/actions/Router";
import { modalOpened, modalClosed } from "../../common/actions/Modal";
import { currentUserSelector, screenSizeSelector, localeSelector } from "../../common/selectors";
import {
  draftCompanySelector,
  draftCompanyAvatarSelector,
  draftCompanyBackgroundSelector,
  isMemberSelector,
  editableSelector,
  editModeSelector,
  isDirtySelector,
} from "../selectors";
import { ModalContainer, ModalRenderFunc } from "../../common/components/modal";
import { findErrorMessageElements } from "../../common/components/ErrorMessage";
import { animatedScrollTo } from "../../lib/animate";
import Header from "./Header";
import EditBasicInfo, { EditBasicInfoModels } from "./EditBasicInfo";
import DraftActions from "./DraftActions";
import SimilarCompaniesSectionContainer from "./SimilarCompaniesSectionContainer";

const browser = detect();

interface OwnProps {
  routeParams: { companyId: string };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

class RootContainer extends React.Component<Props> {
  public static contextTypes = {
    history: PropTypes.object.isRequired,
  };

  public componentDidMount() {
    const { editable, screenSize, editModeStarted } = this.props;
    if (editable && screenSize === "large") {
      // Lazy to set editable
      setTimeout(() => {
        editModeStarted();
      }, 1000);
    }
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (nextProps.editable && this.props.screenSize !== nextProps.screenSize) {
      if (nextProps.editMode && nextProps.screenSize !== "large") {
        nextProps.editModeFinished();
      } else if (!nextProps.editMode && nextProps.screenSize === "large") {
        nextProps.editModeStarted();
      }
    }
  }

  public onOpenEditBasicInfoModal = () => {
    this.props.modalOpened({
      name: "companies/edit-basic-info",
    });
  };

  public onSubmitBasicInfo = (company: EditBasicInfoModels["Company"]) => {
    if (!isEqual(this.props.company, company)) {
      const draft = { ...this.props.company, ...company };
      this.props.draftCompanyUpdated(draft);
    }
    this.props.modalClosed({ name: "companies/edit-basic-info" });
  };

  public onCancelBasicInfo = () => {
    this.props.modalClosed({ name: "companies/edit-basic-info" });
  };

  public onPublishDraft = async () => {
    if (!this.props.isSavingDraft) {
      // TODO(KentoMoriwaki): Fix action types mapped by bindActionCreators
      const result = (await this.props.saveDraftAllWorker()) as any;
      if (isFailure(result)) {
        const elems = findErrorMessageElements();
        if (elems.length > 0) {
          if (browser && (browser.name === "chrome" || browser.name === "firefox")) {
            elems[0].scrollIntoView({ behavior: "smooth", block: "center" });
          } else {
            const top = elems[0].getBoundingClientRect().top;
            const offset = window.pageYOffset + top - window.innerHeight / 2;
            animatedScrollTo(offset, 300);
          }
        }
      }
    }
  };

  public onResetDraft = () => {
    this.props.draftReset();
  };

  public render() {
    const {
      company,
      avatar,
      background,
      editMode,
      isDraftDirty,
      isSavingDraft,
      children,
      draftCompanyAvatarUpdated,
      draftCompanyBackgroundUpdated,
      homeLoaded,
      isMember,
      currentUser,
      locale,
    } = this.props;
    if (!company) {
      return null;
    }
    return (
      <>
        <DraftActions
          isDirty={isDraftDirty}
          isSaving={isSavingDraft}
          locale={locale}
          onPublish={this.onPublishDraft}
          onCancel={this.onResetDraft}
        />
        <Page>
          <Header
            company={company}
            avatar={avatar}
            background={background}
            // Stop fixing the header when editable mode. It could break D&D in members page.
            // Stop fixing the header when unsigned-in because sign-in header is shown fixed.
            canBeFixed={!editMode && Boolean(currentUser)}
            pathname={this.context.history.location.pathname}
            editable={editMode}
            onEdit={this.onOpenEditBasicInfoModal}
            homeLoaded={homeLoaded}
            onChangeAvatar={draftCompanyAvatarUpdated}
            onChangeBackground={draftCompanyBackgroundUpdated}
            isMember={isMember}
          />
          <div>{children}</div>
          <Footer>
            <SimilarCompaniesSectionContainer />
          </Footer>
        </Page>
        <ModalContainer name="companies/edit-basic-info" render={this.renderEditBasicInfoModal} />
      </>
    );
  }

  private renderEditBasicInfoModal: ModalRenderFunc = () => {
    const { company } = this.props;
    return <EditBasicInfo company={company!} onSubmit={this.onSubmitBasicInfo} onCancel={this.onCancelBasicInfo} />;
  };
}

const Page = styled.div`
  background: white;
`;

const Footer = styled.div`
  margin-top: 80px;
  @media ${media.large} {
    margin-top: 100px;
  }
`;

function mapStateToProps(state: RootState) {
  return {
    currentUser: currentUserSelector(state),
    locale: localeSelector(state),
    screenSize: screenSizeSelector(state),
    company: draftCompanySelector(state),
    avatar: draftCompanyAvatarSelector(state) || null,
    background: draftCompanyBackgroundSelector(state) || null,
    isMember: isMemberSelector(state),
    editable: editableSelector(state),
    editMode: editModeSelector(state),
    isDraftDirty: isDirtySelector(state),
    isSavingDraft: state.companies.common.isSavingDraft,
    isEditBasicInfoModalOpen:
      !!state.modals.currentModal && state.modals.currentModal.name === "companies/edit-basic-info",
    homeLoaded: state.companies.show.isLoaded,
  };
}

function mapDispatchToProps(dispatch: Dispatch<RootState>) {
  return bindActionCreators(
    {
      editModeStarted,
      editModeFinished,
      draftCompanyUpdated,
      draftCompanyAvatarUpdated,
      draftCompanyBackgroundUpdated,
      saveDraftAllWorker,
      draftReset,
      modalOpened,
      modalClosed,
      pushState,
    },
    dispatch
  );
}

reducerRegistry.register({ companies: reducer }, [draftChangesMiddleware]);

const connected = connect(mapStateToProps, mapDispatchToProps)(RootContainer);
export default hot(module)(connected);



// WEBPACK FOOTER //
// ./frontend/src/companies/components/RootContainer.tsx